'use client'

import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { BrandEntry } from '../dashboardCatalog'

// Demo mode: hides which brands the platform serves. While it is on, the
// sidebar blurs every brand's mark and shows an id instead of its name, and
// the topbar breadcrumb, the pinned page heading (eyebrow and title) and the
// browser tab say the same id wherever they would have said the brand.
// Nothing else changes -- tables, details and the data underneath keep the
// real names (2026-10-23: "just in the sidebar and the headers of the
// views"). The id is a hash of the brand's label, so a brand reads the same
// in the sidebar, the heading and every reload. Remembered in a cookie
// (tf_brand_mask, a year), read when the toggle starts: the Shell only
// mounts in the browser, so a reload comes up masked from its first frame,
// never flashing the real names.

const COOKIE_NAME = 'tf_brand_mask'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

function readMaskCookie(): boolean {
  try {
    return document.cookie.split('; ').some((c) => c === `${COOKIE_NAME}=1`)
  } catch {
    return false
  }
}

function writeMaskCookie(on: boolean) {
  try {
    document.cookie = `${COOKIE_NAME}=${on ? '1' : '0'}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`
  } catch { /* cookies blocked: the mask just isn't remembered */ }
}

export type BrandMask = {
  masked: boolean
  toggle: () => void
  /** The text with every brand name in it replaced by that brand's id. */
  mask: (text: string) => string
  /** One brand's label as shown: its id while masked. */
  label: (brand: BrandEntry) => string
}

const identity = (text: string) => text

export const BrandMaskContext = createContext<BrandMask>({
  masked: false,
  toggle: () => {},
  mask: identity,
  label: (brand) => brand.label,
})

export function useBrandMask(): BrandMask {
  return useContext(BrandMaskContext)
}

// FNV-1a over the upper-cased label, as 5 base-36 characters: "ID-K3Q9X".
export function brandId(label: string): string {
  let h = 0x811c9dc5
  for (const ch of label.trim().toUpperCase()) {
    h ^= ch.codePointAt(0) ?? 0
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return `ID-${h.toString(36).toUpperCase().padStart(5, '0').slice(-5)}`
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** The Shell's state; it renders BrandMaskContext.Provider with the value. */
export function useBrandMaskState(brands: ReadonlyArray<BrandEntry>): BrandMask {
  const [masked, setMasked] = useState(() => typeof document !== 'undefined' && readMaskCookie())
  const toggle = useCallback(() => setMasked((prev) => {
    writeMaskCookie(!prev)
    return !prev
  }), [])

  // Every name a heading may use for a brand -- its label and its platform
  // codes ("SIBS", "mbway") -- mapped to the label's id; longest first so
  // "Via Verde" wins over a shorter name inside it.
  const names = useMemo(() => {
    const byName = new Map<string, string>()
    for (const b of brands) {
      const id = brandId(b.label)
      for (const n of [b.label, b.platformCode, b.platformNameCode]) {
        if (n && n.trim().length >= 2 && !byName.has(n.toLowerCase())) byName.set(n.toLowerCase(), id)
      }
    }
    return byName
  }, [brands])
  const pattern = useMemo(() => {
    if (names.size === 0) return null
    const alts = [...names.keys()].sort((a, b) => b.length - a.length).map(escapeRe)
    return new RegExp(`(?<![\\p{L}\\p{N}])(${alts.join('|')})(?![\\p{L}\\p{N}])`, 'giu')
  }, [names])

  const mask = useCallback((text: string) => {
    if (!masked || !pattern) return text
    return text.replace(pattern, (m) => names.get(m.toLowerCase()) ?? m)
  }, [masked, pattern, names])
  const label = useCallback((brand: BrandEntry) => (masked ? brandId(brand.label) : brand.label), [masked])

  return useMemo(() => ({ masked, toggle, mask, label }), [masked, toggle, mask, label])
}
